<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

/**
 * The trail of a page, built from the route's `meta.breadcrumb` (see router/index.js): the area
 * ("Backend"), the section, then every step the page sits under. The last step is the page's own
 * title — the one the header already shows — and it is never a link.
 *
 * `meta.breadcrumb` lists the steps ABOVE the page, each one `{ key, to }`: the translation key of
 * its label and the route that reaches it. The current route's params are merged into `to` and into
 * the labels, so a step can be written without repeating the id ("Segnalazione #{id}" needs no
 * plumbing in the router).
 *
 * A route without `meta.breadcrumb` renders nothing: the component is inert where it was not meant
 * to appear, which is what lets the header use it unconditionally.
 */
const props = defineProps({
  steps: {
    type: Array,
    default: () => []
  },
  current: {
    type: String,
    default: ''
  }
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const items = computed(() => {
  const params = { ...route.params }

  const trail = props.steps.map(step => ({
    title: t(step.key, params),
    to: router.resolve({ ...step.to, params: { ...step.to.params, ...params } }).fullPath
  }))

  // The page itself: no `to`, so it is not a link, and `active` is what marks it as the current one
  // (Vuetify puts aria-current on it); `disabled` only greys it, as a step that is not going anywhere.
  if (props.current) trail.push({ title: props.current, active: true, disabled: true })

  return trail
})
</script>

<template>
  <!-- One step alone is the page itself: there is no trail to show. -->
  <v-breadcrumbs
    v-if="items.length > 1"
    :items="items"
    density="compact"
    class="pa-0 mb-2 text-body-2"
  />
</template>
